'use client';

import * as React from 'react';

import type { CityCode } from '@loci/city-editions';

import { cn } from '@/components/atoms/primitives/utils';
import { DiscoverActiveFilters } from '@/components/molecules/DiscoverActiveFilters';
import { DiscoverCategoryFilter } from '@/components/molecules/DiscoverCategoryFilter';
import { DiscoverDatePills } from '@/components/molecules/DiscoverDatePills';
import { DiscoverFollowToggle } from '@/components/molecules/DiscoverFollowToggle';
import { DiscoverNeighborhoodFilter } from '@/components/molecules/DiscoverNeighborhoodFilter';
import { DiscoverPricePresets } from '@/components/molecules/DiscoverPricePresets';
import { DiscoverScopeCaption } from '@/components/molecules/DiscoverScopeCaption';

import { DiscoverRailHeadCards } from './DiscoverRailHeadCards';
/* ------------------------------------------------------------------ */
/*  View — pure layout shell                                           */
/* ------------------------------------------------------------------ */

export interface DiscoverFilterRailViewProps {
  /**
   * Taste-match summary card (FRE-911) rendered at the very TOP of the rail,
   * above the active-filters row. Optional so the View keeps rendering
   * standalone in snapshots/stories that predate the card; the connected rail
   * supplies `<DiscoverRailHeadCards />` (taste card for a signed-in viewer
   * with a real taste signal; WEB-937 access card for an anonymous viewer).
   * Each card returns null when it does not apply, so a signed-in viewer
   * without taste still sees neither. Sibling rail issues (FRE-913/914/916)
   * add their own slots below this one.
   */
  readonly tasteCardSlot?: React.ReactNode;
  /** Active-filters chip row (rendered above the first filter section). */
  readonly activeFiltersSlot: React.ReactNode;
  /** Date filter pills (Tonight / Tomorrow / Weekend / N7 / N30 / Custom). */
  readonly dateSlot: React.ReactNode;
  /**
   * Scope caption (WEB-659 / DPL-09) — "10 categories · 47 events tonight
   * citywide" — anchoring the Category facet counts to a stated scope.
   * Rendered directly above the category checklist. Optional so the View
   * keeps rendering standalone in stories/snapshots that predate it; the
   * connected rail supplies `<DiscoverScopeCaption />`.
   */
  readonly scopeCaptionSlot?: React.ReactNode;
  /** Category facet checklist. */
  readonly categorySlot: React.ReactNode;
  /** Neighborhood facet checklist. */
  readonly neighborhoodSlot: React.ReactNode;
  /** Price preset selector — Free / $0–25 / $25+ (WEB-656 / DPL-06). */
  readonly priceSlot: React.ReactNode;
  /**
   * "Only who I follow" toggle (WEB-662 / DPL-12) rendered BELOW the filter
   * sections, in the rail's "personalized / followed" tail — the position the
   * two retired follow rails used to occupy (design-system `disc-follow`,
   * discover.html L978–1001).
   *
   * Replaces the former `followedArtistsSlot` + `followedVenuesSlot` pair. Two
   * list sections became one switch: the artists rail never filtered anything
   * (there was no artist-id filter branch server-side) and the venues rail made
   * the viewer assemble a filter out of client-held ids. API-1664's
   * server-resolved `followedOnly` covers both dimensions at once.
   *
   * Optional so the View keeps rendering standalone in snapshots/stories; the
   * connected rail supplies `<DiscoverFollowToggle />`, which itself renders
   * nothing for anonymous viewers or viewers who follow nobody (honest-absence
   * contract, inherited unchanged from both retired rails).
   */
  readonly followToggleSlot?: React.ReactNode;
  /** Wrapper class names (e.g. for layout / spacing overrides). */
  readonly className?: string;
}

interface RailSectionProps {
  /** Optional heading rendered when the slot has no internal `<h3>` of its own. */
  readonly heading?: string;
  /** Stable `data-testid` for the section wrapper. */
  readonly testId: string;
  readonly children: React.ReactNode;
}

function RailSection({ heading, testId, children }: RailSectionProps): React.ReactElement {
  const headingId = heading ? `${testId}-heading` : undefined;
  return (
    <section
      data-testid={testId}
      className="disc-filter flex flex-col gap-2"
      aria-labelledby={headingId}
    >
      {heading ? (
        <h3 id={headingId} className="px-1 text-sm font-semibold text-foreground">
          {heading}
        </h3>
      ) : null}
      <div className="disc-filter__body">{children}</div>
    </section>
  );
}

/**
 * Pure presentational shell for the Discover filter rail. Renders an
 * active-filters chip row above a vertical stack of filter sections in the
 * order specified by the design source — ActiveFilters → Date → Category →
 * Neighborhood → Price → Follow. Each slot is a `ReactNode` so
 * stories and tests can substitute stub filters without an RTK Query
 * provider or Next.js router.
 *
 * WEB-1228 (ruling R20): the saved-views UI is deleted — neither the "Save
 * this view" action nor the saved-views list exists any more.
 *
 * WEB-662 (DPL-12): the two follow rails at the bottom of this rail —
 * `DiscoverFollowedArtistsRail` (FRE-913) and `DiscoverFollowedVenuesRail`
 * (FRE-914/FRE-1145) — are RETIRED and DELETED, collapsed into the single
 * `DiscoverFollowToggle` ("Only who I follow"). Both molecules are gone from
 * the tree; this rail and `MobileFilterDrawer` were their only consumers. The
 * `venue` URL key they wrote survives (still parsed, serialized, and honoured
 * end-to-end) so existing `?venue=` deep links and saved searches keep
 * narrowing — see `lib/discover/url-state.ts`.
 *
 * WEB-657 (DPL-07): the radius (distance) slider section is retired. The
 * `/discover` query is centered on the city anchor, not the viewer, so a
 * radius control implied a personal center the query never had. Distance now
 * stays visible only where it's honest — on each event row and as the
 * Distance sort option (`DiscoverResultsBar.tsx`).
 *
 * WEB-1228 (M3 wave 0, rulings R7 / R8): the Vibes section, the time-of-day
 * blocks and the Genre facet are deleted. None of them filtered on real data,
 * so Discover no longer offers them.
 *
 * Sticky + independent scroll (WEB-767): the *shell* `<aside>` owns
 * `DISCOVER_RAIL_STICKY_CLASS` (see `templates/DiscoverTemplate/chromeOffsets.ts`).
 * This View is the filter stack only — do not re-apply sticky / max-height /
 * overflow here or the Sidebar grows a nested scrollport. WEB-651 (DPL-01):
 * the stuck offset on that aside comes from `--nav-stick-top`, not the
 * pre-chrome `top-4` literal that parked the first control behind the 82px
 * app-chrome band.
 *
 * Column width (WEB-655 / DPL-05): the rail owns NO width of its own — it fills
 * the shell grid column that `DiscoverTemplate` sizes (288px at 981–1279, 336px
 * from 1280), minus the rail wrapper's padding. Do not reintroduce a `w-*` here:
 * a fixed width would silently win over the grid template and desync the two
 * numbers. Available content width is therefore ~235px / ~267px respectively —
 * measurably narrower than the pre-v2 280px column at tablet widths and wider at
 * desktop, so child molecules should stay fluid rather than assume either.
 *
 * Visibility (WEB-273 / WEB-699): the inline rail is hidden at ≤980px and the
 * `MobileFilterDrawer` mounted by `DiscoverTemplate` takes over. The
 * `hidden min-[981px]:flex` rule lives on the rail's own root so the
 * `<aside>` wrapper that announces the "Discover filters" landmark stays in
 * the SSR markup (preventing hydration drift) while the visual rail only
 * paints when the column actually has room.
 */
export function DiscoverFilterRailView({
  tasteCardSlot,
  activeFiltersSlot,
  dateSlot,
  scopeCaptionSlot,
  categorySlot,
  neighborhoodSlot,
  priceSlot,
  followToggleSlot,
  className,
}: DiscoverFilterRailViewProps): React.ReactElement {
  return (
    <div
      data-testid="discover-filter-rail"
      className={cn('hidden flex-col gap-6', 'min-[981px]:flex', className)}
    >
      {tasteCardSlot ? (
        // `empty:hidden` collapses the slot when its connected card renders
        // nothing (anonymous / signal-less viewers) so the rail's `gap-6` flow
        // doesn't reserve whitespace at the top for an invisible element.
        <div data-testid="discover-filter-rail-taste-card" className="empty:hidden">
          {tasteCardSlot}
        </div>
      ) : null}

      <div data-testid="discover-filter-rail-active-filters">{activeFiltersSlot}</div>

      <RailSection heading="Date" testId="discover-filter-rail-date">
        {dateSlot}
      </RailSection>

      <RailSection testId="discover-filter-rail-category">
        {scopeCaptionSlot ? (
          <div data-testid="discover-filter-rail-scope-caption">{scopeCaptionSlot}</div>
        ) : null}
        {categorySlot}
      </RailSection>

      <RailSection testId="discover-filter-rail-neighborhood">{neighborhoodSlot}</RailSection>

      <RailSection testId="discover-filter-rail-price">{priceSlot}</RailSection>

      {followToggleSlot ? (
        // `empty:hidden` collapses the slot when the connected toggle renders
        // nothing (anonymous viewers / viewers following nobody) so the rail's
        // `gap-6` flow doesn't reserve whitespace for an invisible control. The
        // molecule owns its own label, so no `RailSection` heading here.
        <div data-testid="discover-filter-rail-follow-toggle" className="empty:hidden">
          {followToggleSlot}
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Connected — wires View to the canonical /discover URL state        */
/* ------------------------------------------------------------------ */

export interface DiscoverFilterRailProps {
  /**
   * Active city — forwarded to the facet molecules so each facets request
   * carries the city geo anchor (WEB-263). Without this the rail computes
   * facets globally and the chip counts can include cross-country events.
   */
  readonly cityCode: CityCode;
  /**
   * SSR envelope total forwarded to the anonymous access card so `{N}`
   * matches the results bar on first paint (WEB-786 / WEB-937).
   */
  readonly initialTotal?: number;
  /** Serialized SSR discover-args scope for the access-card `{N}` fallback. */
  readonly initialTotalsScope?: string;
  /** Wrapper class names forwarded to the View. */
  readonly className?: string;
}

/**
 * Connected wrapper for the Discover filter rail (WEB-63). Composes the filter
 * molecules — date, category, neighborhood, price and the follow toggle —
 * into a single rail backed by
 * `useDiscoverParams` URL state.
 *
 * Each child molecule is independently connected — the rail itself only
 * provides layout and the active-filters row. URL state is the single source
 * of truth, so deep-links and back/forward navigation round-trip cleanly
 * without organism-level state.
 *
 * Mobile note: the M2.9 drawer variant takes over below the desktop
 * breakpoint. Per WEB-63 / WEB-767, sticky + independent scroll live on the
 * shell aside, not on this organism.
 */
export function DiscoverFilterRail({
  cityCode,
  initialTotal,
  initialTotalsScope,
  className,
}: DiscoverFilterRailProps): React.ReactElement {
  return (
    <DiscoverFilterRailView
      className={className}
      tasteCardSlot={
        <DiscoverRailHeadCards
          cityCode={cityCode}
          initialTotal={initialTotal}
          initialTotalsScope={initialTotalsScope}
        />
      }
      activeFiltersSlot={<DiscoverActiveFilters cityCode={cityCode} />}
      dateSlot={<DiscoverDatePills />}
      scopeCaptionSlot={<DiscoverScopeCaption cityCode={cityCode} />}
      categorySlot={<DiscoverCategoryFilter cityCode={cityCode} />}
      neighborhoodSlot={<DiscoverNeighborhoodFilter cityCode={cityCode} />}
      priceSlot={
        <DiscoverPricePresets cityCode={cityCode} id="discover-filter-rail-price-presets" />
      }
      followToggleSlot={
        <DiscoverFollowToggle cityCode={cityCode} id="discover-filter-rail-follow-toggle-switch" />
      }
    />
  );
}
